import {useAxiosAuth} from "@/hooks/useAxiosAuth";
import {useMutation} from "@tanstack/react-query";
import {ApiConfig} from "@/config/api.config.ts";
import {ErrorKey, getErrorKey} from "@/config/apiErrorKeys.ts";
import {AxiosError} from "axios";
import {ProblemDetails} from "@/@types/base.types.ts";
import EToast from '@/components/everridge/EToast';
import { useTranslation } from 'react-i18next';
import { RegisterGetLinkRequestDto } from '@/features/identity/types/identity-dto';
import { RequestHeaders } from '@/@types/auth.types';

type Props = {
    tenant: string;
    onSuccess?: () => void;
    onError?: (error: ErrorKey) => void;
}

export const useSignUpApi = ({ tenant, onSuccess, onError }: Props) => {
    const { t } = useTranslation();
    const axiosInstance = useAxiosAuth();
    axiosInstance.defaults.headers[RequestHeaders.TenantId] = tenant;

    const {mutateAsync: signUp, isPending: isDoSignUpPending} = useMutation({
        mutationFn: async ({ email, password }: RegisterGetLinkRequestDto) => {
            const url = ApiConfig.queries.auth.endpoints.singUp;
            const response = await axiosInstance.post(url, { email, password });
            return response.data;
        },
        onSuccess: () => {
            onSuccess 
                ?   onSuccess?.() 
                :   EToast({
                        message: t('auth.signUpForm.toasts.success.message'),
                        description: t('auth.signUpForm.toasts.success.description'),
                        variant: 'success',
                    });
        },
        onError: (error: AxiosError<ProblemDetails>) => {
            onError 
                ?   onError?.(getErrorKey(error))
                :   EToast({
                        message: t('auth.signUpForm.toasts.error.message'),
                        description: t('auth.signUpForm.toasts.error.description'),
                        variant: 'error',
                    });
        }
    })

    return {
        signUp,
        isDoSignUpPending
    }
}